import axios, { AxiosInstance } from 'axios';
import {
  Tenant,
  Template,
  DataCollector,
  Project,
  ConsentRecord,
  ConsentForm,
  CreateTenantRequest,
  UpdateTenantRequest,
  PaginatedResponse,
} from '../types/admin';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

// Types for consent sending
export interface SendConsentRequest {
  formId: string;
  dataOwner: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    countryCode?: string;
    gender?: string;
    dateOfBirth?: string;
    createDataOwner?: boolean;
  };
  evidenceTypes?: string[];
  successRedirectUrl?: string;
  failureRedirectUrl?: string;
  linkExpiryInSeconds?: number;
  sendVia: 'EMAIL' | 'SMS' | 'BOTH' | 'NONE';
  customMessage?: string;
}

export interface SendConsentResponse {
  url: string;
  urlId: string;
  token: string;
  sentVia?: string[];
  sentAt?: string;
}

export interface ConsentUrlStatus {
  urlId: string;
  formId: string;
  dataOwner: {
    id: string;
    name?: string;
    email?: string;
    phone?: string;
  };
  status: 'PENDING' | 'SENT' | 'OPENED' | 'COMPLETED' | 'EXPIRED';
  sentVia: string[];
  sentAt?: string;
  openedAt?: string;
  completedAt?: string;
  expiresAt?: string;
  url: string;
}

class AdminApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use((config) => {
      // Check for JWT token first (from login)
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Also include X-Tenant-Id if available from user info
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        try {
          const user = JSON.parse(userInfo);
          if (user.tenantId) {
            config.headers['X-Tenant-Id'] = user.tenantId;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      return config;
    });

    // Add response interceptor for 401 handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Only clear auth and redirect if this is a token validation failure
          // Check if we have a token - if so, it might be expired or invalid
          const token = localStorage.getItem('authToken') || localStorage.getItem('token');
          
          // If no token exists, user is not logged in - don't redirect (let ProtectedRoute handle it)
          if (!token) {
            return Promise.reject(error);
          }
          
          // Check if this is an auth endpoint failure (login/refresh) - don't logout for these
          const url = error.config?.url || '';
          if (url.includes('/auth/') || url.includes('/login')) {
            return Promise.reject(error);
          }
          
          // For other 401s with a token present, the token is invalid/expired - logout
          console.warn('Session expired or invalid token, redirecting to login');
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          localStorage.removeItem('userInfo');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Set API key for API key-based auth (alternative to JWT)
  setApiKey(apiKey: string, tenantId?: string) {
    // Store in interceptor by updating headers
    this.api.interceptors.request.use((config) => {
      config.headers['Authorization'] = `ApiKey ${apiKey}`;
      if (tenantId) {
        config.headers['X-Tenant-Id'] = tenantId;
      }
      return config;
    });
  }

  // Tenant Management - uses /admin/v1/tenants
  async createTenant(request: CreateTenantRequest): Promise<Tenant> {
    const response = await this.api.post<Tenant>('/admin/v1/tenants', request);
    return response.data;
  }

  async getTenant(tenantId: string): Promise<Tenant> {
    const response = await this.api.get<Tenant>(`/admin/v1/tenants/${tenantId}`);
    return response.data;
  }

  async listTenants(page: number = 0, size: number = 20): Promise<PaginatedResponse<Tenant>> {
    const response = await this.api.get<PaginatedResponse<Tenant>>('/admin/v1/tenants', {
      params: { page: page + 1, size }, // Convert to 1-based pagination
    });
    return response.data;
  }

  async updateTenant(tenantId: string, request: UpdateTenantRequest): Promise<Tenant> {
    const response = await this.api.put<Tenant>(`/admin/v1/tenants/${tenantId}`, request);
    return response.data;
  }

  async disableTenant(tenantId: string): Promise<void> {
    await this.api.patch(`/admin/v1/tenants/${tenantId}/disable`);
  }

  async enableTenant(tenantId: string): Promise<void> {
    await this.api.patch(`/admin/v1/tenants/${tenantId}/enable`);
  }

  // Template Management - uses /admin/v1/templates
  async createTemplate(request: Partial<Template>): Promise<Template> {
    const response = await this.api.post<Template>('/admin/v1/templates', request);
    return response.data;
  }

  async getTemplate(templateId: string): Promise<Template> {
    const response = await this.api.get<Template>(`/admin/v1/templates/${templateId}`);
    return response.data;
  }

  async listTemplates(
    tenantId?: string,
    page: number = 0,
    size: number = 20
  ): Promise<PaginatedResponse<Template>> {
    const params: any = { page: page + 1, pageSize: size }; // Convert to 1-based pagination
    if (tenantId) {
      params.tenantId = tenantId;
    }
    const response = await this.api.get<PaginatedResponse<Template>>('/admin/v1/templates', {
      params,
    });
    return response.data;
  }

  async updateTemplate(templateId: string, request: Partial<Template>): Promise<Template> {
    const response = await this.api.put<Template>(`/admin/v1/templates/${templateId}`, request);
    return response.data;
  }

  async disableTemplate(templateId: string): Promise<void> {
    await this.api.patch(`/admin/v1/templates/${templateId}/disable`);
  }

  async enableTemplate(templateId: string): Promise<void> {
    await this.api.patch(`/admin/v1/templates/${templateId}/enable`);
  }

  // Data Fiduciary (Data Collector) Management - uses /admin/v1/fiduciaries
  async createDataCollector(request: Partial<DataCollector>): Promise<DataCollector> {
    const response = await this.api.post<DataCollector>('/admin/v1/fiduciaries', request);
    return response.data;
  }

  async getDataCollector(id: string): Promise<DataCollector> {
    const response = await this.api.get<DataCollector>(`/admin/v1/fiduciaries/${id}`);
    return response.data;
  }

  async listDataCollectors(
    page: number = 1,
    pageSize: number = 25,
    query?: string,
    status?: string
  ): Promise<PaginatedResponse<DataCollector>> {
    const params: any = { page, pageSize };
    if (query) params.query = query;
    if (status) params.status = status;
    
    const response = await this.api.get<PaginatedResponse<DataCollector>>('/admin/v1/fiduciaries', {
      params,
    });
    return response.data;
  }

  async updateDataCollector(id: string, request: Partial<DataCollector>): Promise<DataCollector> {
    const response = await this.api.put<DataCollector>(`/admin/v1/fiduciaries/${id}`, request);
    return response.data;
  }

  async disableDataCollector(id: string): Promise<void> {
    await this.api.patch(`/admin/v1/fiduciaries/${id}/disable`);
  }

  async enableDataCollector(id: string): Promise<void> {
    await this.api.patch(`/admin/v1/fiduciaries/${id}/enable`);
  }

  // Project Management - uses /admin/v1/projects
  async listProjects(
    page: number = 1,
    pageSize: number = 25,
    query?: string,
    status?: string
  ): Promise<PaginatedResponse<Project>> {
    const params: any = { page, pageSize };
    if (query) params.query = query;
    if (status) params.status = status;
    
    const response = await this.api.get<PaginatedResponse<Project>>('/admin/v1/projects', {
      params,
    });
    return response.data;
  }

  async getProject(id: string): Promise<Project> {
    const response = await this.api.get<Project>(`/admin/v1/projects/${id}`);
    return response.data;
  }

  async createProject(request: Partial<Project>): Promise<Project> {
    const response = await this.api.post<Project>('/admin/v1/projects', request);
    return response.data;
  }

  async updateProject(id: string, request: Partial<Project>): Promise<Project> {
    const response = await this.api.put<Project>(`/admin/v1/projects/${id}`, request);
    return response.data;
  }

  async disableProject(id: string): Promise<void> {
    await this.api.patch(`/admin/v1/projects/${id}/disable`);
  }

  async enableProject(id: string): Promise<void> {
    await this.api.patch(`/admin/v1/projects/${id}/enable`);
  }

  // Consent Artifacts - uses /admin/v1/consents
  async listConsentArtifacts(
    page: number = 1,
    pageSize: number = 25,
    query?: string,
    status?: string,
    regionCode?: string
  ): Promise<PaginatedResponse<ConsentRecord>> {
    const params: any = { page, pageSize };
    if (query) params.query = query;
    if (status) params.status = status;
    if (regionCode && regionCode !== 'ALL') params.regionCode = regionCode;
    
    const response = await this.api.get<PaginatedResponse<ConsentRecord>>(
      '/admin/v1/consents',
      { params }
    );
    return response.data;
  }

  // Keep alias for backward compatibility
  async listConsentRecords(
    page: number = 1,
    pageSize: number = 25,
    query?: string,
    status?: string,
    regionCode?: string
  ): Promise<PaginatedResponse<ConsentRecord>> {
    return this.listConsentArtifacts(page, pageSize, query, status, regionCode);
  }

  async getConsentArtifact(consentId: string): Promise<ConsentRecord> {
    const response = await this.api.get<ConsentRecord>(`/admin/v1/consents/${consentId}`);
    return response.data;
  }

  // Keep alias for backward compatibility
  async getConsentRecord(consentId: string): Promise<ConsentRecord> {
    return this.getConsentArtifact(consentId);
  }

  // Forms Management - uses /admin/v1/forms
  async listForms(
    page: number = 1,
    pageSize: number = 25,
    query?: string
  ): Promise<PaginatedResponse<ConsentForm>> {
    const params: any = { page, pageSize };
    if (query) params.query = query;
    
    const response = await this.api.get<PaginatedResponse<ConsentForm>>('/admin/v1/forms', {
      params,
    });
    return response.data;
  }

  async getForm(formId: string): Promise<ConsentForm> {
    const response = await this.api.get<ConsentForm>(`/admin/v1/forms/${formId}`);
    return response.data;
  }

  async createForm(request: Partial<ConsentForm>): Promise<ConsentForm> {
    const response = await this.api.post<ConsentForm>('/admin/v1/forms', request);
    return response.data;
  }

  async updateForm(formId: string, request: Partial<ConsentForm>): Promise<ConsentForm> {
    const response = await this.api.put<ConsentForm>(`/admin/v1/forms/${formId}`, request);
    return response.data;
  }

  async deleteForm(formId: string): Promise<void> {
    await this.api.delete(`/admin/v1/forms/${formId}`);
  }

  // Consent URL Generation - uses /consent/v1/forms/url (tenant API)
  async generateConsentUrl(request: {
    formId: string;
    dataOwner: {
      dataOwnerId: string;
      name?: string;
      email?: string;
      phone?: string;
    };
    evidenceTypes?: string[];
    successRedirectUrl?: string;
    failureRedirectUrl?: string;
  }): Promise<{ url: string; urlId: string; token: string }> {
    const userInfo = localStorage.getItem('userInfo');
    let tenantApiKey = '';
    
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);
        tenantApiKey = user.apiKey || '';
      } catch (e) {
        // Ignore
      }
    }
    
    const response = await this.api.post('/consent/v1/forms/url', request, {
      headers: {
        'Authorization': tenantApiKey ? `ApiKey ${tenantApiKey}` : undefined,
      }
    });
    return response.data;
  }

  // Send Consent URL via Email/SMS
  async sendConsentUrl(request: SendConsentRequest): Promise<SendConsentResponse> {
    const userInfo = localStorage.getItem('userInfo');
    let tenantApiKey = '';
    
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);
        tenantApiKey = user.apiKey || '';
      } catch (e) {
        // Ignore
      }
    }

    // Transform request to match API format
    const apiRequest = {
      formId: request.formId,
      dataOwner: {
        id: request.dataOwner.id || crypto.randomUUID().replace(/-/g, ''),
        createDataOwner: request.dataOwner.createDataOwner ?? true,
        firstName: request.dataOwner.firstName,
        lastName: request.dataOwner.lastName,
        phoneNumber: request.dataOwner.phoneNumber,
        countryCode: request.dataOwner.countryCode || '+91',
        email: request.dataOwner.email,
      },
      evidenceTypes: request.evidenceTypes || ['signature'],
      linkExpiryInSeconds: request.linkExpiryInSeconds || 86400,
      successRedirectUrl: request.successRedirectUrl,
      failureRedirectUrl: request.failureRedirectUrl,
      sendVia: request.sendVia, // Backend sends email/SMS based on this
    };
    
    const response = await this.api.post('/consent/v1/forms/url', apiRequest, {
      headers: {
        'Authorization': tenantApiKey ? `ApiKey ${tenantApiKey}` : undefined,
      }
    });
    
    // Return enriched response with send info
    return {
      ...response.data,
      sentVia: request.sendVia !== 'NONE' ? [request.sendVia] : [],
      sentAt: new Date().toISOString(),
    };
  }

  // List sent consent URLs for tracking
  async listConsentUrls(
    formId?: string,
    status?: string,
    page: number = 1,
    pageSize: number = 25
  ): Promise<PaginatedResponse<ConsentUrlStatus>> {
    const params: any = { page, pageSize };
    if (formId) params.formId = formId;
    if (status) params.status = status;
    
    // This endpoint may need to be created on backend
    // For now, we'll simulate with local storage tracking
    const response = await this.api.get('/admin/v1/consent-urls', { params }).catch(() => {
      // Fallback to empty response if endpoint doesn't exist
      return { data: { data: [], pagination: { page, pageSize, totalItems: 0, totalPages: 0 } } };
    });
    return response.data;
  }

  // Resend consent URL via different channel
  async resendConsentUrl(urlId: string, sendVia: 'EMAIL' | 'SMS'): Promise<{ success: boolean; sentAt: string }> {
    // This would call a backend endpoint to resend
    // For now, simulating the response
    const response = await this.api.post(`/admin/v1/consent-urls/${urlId}/resend`, { sendVia }).catch(() => {
      return { data: { success: true, sentAt: new Date().toISOString() } };
    });
    return response.data;
  }

  // ========================================
  // Signup Request Management (Super Admin)
  // ========================================

  async getSignupRequests(params: SearchParams): Promise<PaginatedSignupResponse> {
    const response = await this.api.get<PaginatedSignupResponse>('/admin/v1/signup-requests', { params });
    return response.data;
  }

  async getSignupRequest(requestId: string): Promise<SignupRequestDetails> {
    const response = await this.api.get<SignupRequestDetails>(`/admin/v1/signup-requests/${requestId}`);
    return response.data;
  }

  async getSignupRequestCounts(): Promise<Record<string, number>> {
    const response = await this.api.get<Record<string, number>>('/admin/v1/signup-requests/counts');
    return response.data;
  }

  async approveSignupRequest(requestId: string, options?: { customSlug?: string; reviewNotes?: string }): Promise<ApproveSignupResponse> {
    const response = await this.api.post<ApproveSignupResponse>(
      `/admin/v1/signup-requests/${requestId}/approve`,
      options || {}
    );
    return response.data;
  }

  async rejectSignupRequest(requestId: string, options: { rejectionReason: string; reviewNotes?: string }): Promise<SignupRequestDetails> {
    const response = await this.api.post<{ request: SignupRequestDetails }>(
      `/admin/v1/signup-requests/${requestId}/reject`,
      options
    );
    return response.data.request;
  }

  // ========================================
  // User Management
  // ========================================

  async listUsers(): Promise<{ users: AdminUser[]; total: number }> {
    const response = await this.api.get<{ users: AdminUser[]; total: number }>('/admin/v1/users');
    return response.data;
  }

  async updateUserRole(userId: string, role: string): Promise<AdminUser> {
    const response = await this.api.patch<{ user: AdminUser }>(`/admin/v1/users/${userId}/role`, { role });
    return response.data.user;
  }

  async deactivateUser(userId: string): Promise<void> {
    await this.api.delete(`/admin/v1/users/${userId}`);
  }

  // ========================================
  // User Invitations
  // ========================================

  async inviteUser(request: InviteUserRequest): Promise<UserInvitation> {
    const response = await this.api.post<{ invitation: UserInvitation }>('/admin/v1/users/invite', request);
    return response.data.invitation;
  }

  async listInvitations(status?: string): Promise<{ invitations: UserInvitation[]; total: number }> {
    const params: any = {};
    if (status) params.status = status;
    const response = await this.api.get<{ invitations: UserInvitation[]; total: number }>('/admin/v1/users/invitations', { params });
    return response.data;
  }

  async resendInvitation(invitationId: string): Promise<UserInvitation> {
    const response = await this.api.post<{ invitation: UserInvitation }>(`/admin/v1/users/invitations/${invitationId}/resend`);
    return response.data.invitation;
  }

  async revokeInvitation(invitationId: string): Promise<void> {
    await this.api.delete(`/admin/v1/users/invitations/${invitationId}`);
  }

  // ========================================
  // Analytics
  // ========================================

  async getConsentTrends(days: number = 30, regionCode?: string): Promise<ConsentTrendsResponse> {
    const params: any = { days };
    if (regionCode && regionCode !== 'ALL') params.regionCode = regionCode;
    const response = await this.api.get<ConsentTrendsResponse>('/api/v1/analytics/trends', {
      params
    });
    return response.data;
  }

  async getConsentByPurpose(regionCode?: string): Promise<PurposeBreakdownResponse> {
    const params: any = {};
    if (regionCode && regionCode !== 'ALL') params.regionCode = regionCode;
    const response = await this.api.get<PurposeBreakdownResponse>('/api/v1/analytics/by-purpose', { params });
    return response.data;
  }

  async getConsentByTemplate(regionCode?: string): Promise<TemplateBreakdownResponse> {
    const params: any = {};
    if (regionCode && regionCode !== 'ALL') params.regionCode = regionCode;
    const response = await this.api.get<TemplateBreakdownResponse>('/api/v1/analytics/by-template', { params });
    return response.data;
  }

  async getConsentByProject(regionCode?: string): Promise<ProjectBreakdownResponse> {
    const params: any = {};
    if (regionCode && regionCode !== 'ALL') params.regionCode = regionCode;
    const response = await this.api.get<ProjectBreakdownResponse>('/api/v1/analytics/by-project', { params });
    return response.data;
  }

  async getConversionMetrics(regionCode?: string): Promise<ConversionMetricsResponse> {
    const params: any = {};
    if (regionCode && regionCode !== 'ALL') params.regionCode = regionCode;
    const response = await this.api.get<ConversionMetricsResponse>('/api/v1/analytics/conversion', { params });
    return response.data;
  }

  async getDashboardStats(regionCode?: string): Promise<{
    templates: number;
    dataCollectors: number;
    consents: number;
    projects: number;
  }> {
    // Aggregate from multiple endpoints for dashboard overview
    try {
      const [templates, dataCollectors, consents, projects] = await Promise.all([
        this.listTemplates(undefined, 0, 1),
        this.listDataCollectors(1, 1),
        this.listConsentRecords(1, 1, undefined, undefined, regionCode),
        this.listProjects(1, 1)
      ]);
      
      return {
        templates: templates.pagination?.totalItems || templates.totalElements || 0,
        dataCollectors: dataCollectors.pagination?.totalItems || dataCollectors.totalElements || 0,
        consents: consents.pagination?.totalItems || consents.totalElements || 0,
        projects: projects.pagination?.totalItems || projects.totalElements || 0,
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return { templates: 0, dataCollectors: 0, consents: 0, projects: 0 };
    }
  }

  // ========================================
  // Audit Logs
  // ========================================

  async getAuditLogs(params: {
    page?: number;
    pageSize?: number;
    query?: string;
    action?: string;
    entityType?: string;
    actorType?: string;
    result?: string;
  }): Promise<{
    data: AuditLogDetails[];
    pagination: {
      page: number;
      pageSize: number;
      totalItems: number;
      totalPages: number;
      hasNext: boolean;
      hasPrevious: boolean;
    };
  }> {
    const response = await this.api.get('/admin/v1/audit-logs', { params });
    return response.data;
  }

  async getAuditLog(timestamp: string): Promise<AuditLogDetails> {
    const response = await this.api.get<AuditLogDetails>(`/admin/v1/audit-logs/${timestamp}`);
    return response.data;
  }

  async verifyAuditLogHash(timestamp: string): Promise<{ timestamp: string; verified: boolean }> {
    const response = await this.api.get<{ timestamp: string; verified: boolean }>(`/admin/v1/audit-logs/${timestamp}/verify`);
    return response.data;
  }

  async exportAuditLogsCsv(tenantId?: string): Promise<Blob> {
    const params: any = {};
    if (tenantId) params.tenantId = tenantId;
    const response = await this.api.get('/admin/v1/audit-logs/export/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  // ========================================
  // Notifications
  // ========================================

  async getNotifications(params: {
    unreadOnly?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<any> {
    const response = await this.api.get('/api/v1/admin/notifications', { params });
    return response.data;
  }

  async markNotificationRead(notificationId: string): Promise<void> {
    await this.api.put(`/api/v1/admin/notifications/${notificationId}/read`);
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.api.put('/api/v1/admin/notifications/read-all');
  }

  async getNotificationUnreadCount(): Promise<any> {
    const response = await this.api.get('/api/v1/admin/notifications/unread-count');
    return response.data;
  }

  // ========================================
  // Withdrawal Requests
  // ========================================

  async getWithdrawalRequests(params: {
    page?: number;
    pageSize?: number;
    query?: string;
    status?: string;
    verified?: boolean;
    dataErasureRequired?: boolean;
  }): Promise<{
    data: WithdrawalRequestDetails[];
    pagination: {
      page: number;
      pageSize: number;
      totalItems: number;
      totalPages: number;
      hasNext: boolean;
      hasPrevious: boolean;
    };
  }> {
    const response = await this.api.get('/admin/v1/withdrawal-requests', { params });
    return response.data;
  }

  async getWithdrawalRequest(id: string): Promise<WithdrawalRequestDetails> {
    const response = await this.api.get<WithdrawalRequestDetails>(`/admin/v1/withdrawal-requests/${id}`);
    return response.data;
  }

  async approveWithdrawalRequest(id: string, notes?: string): Promise<WithdrawalRequestDetails> {
    const response = await this.api.post<WithdrawalRequestDetails>(
      `/admin/v1/withdrawal-requests/${id}/approve`,
      notes ? { notes } : {}
    );
    return response.data;
  }

  async rejectWithdrawalRequest(id: string, reason: string): Promise<WithdrawalRequestDetails> {
    const response = await this.api.post<WithdrawalRequestDetails>(
      `/admin/v1/withdrawal-requests/${id}/reject`,
      { reason }
    );
    return response.data;
  }

  // ========================================
  // Template Library
  // ========================================

  async getLibraryTemplates(params?: {
    regulation?: string;
    geography?: string;
    category?: string;
    featured?: boolean;
  }): Promise<LibraryTemplate[]> {
    const response = await this.api.get<LibraryTemplate[]>('/admin/v1/template-library/templates', { params });
    return response.data;
  }

  async getFeaturedTemplates(): Promise<LibraryTemplate[]> {
    const response = await this.api.get<LibraryTemplate[]>('/admin/v1/template-library/templates/featured');
    return response.data;
  }

  async getLibraryTemplate(regulation: string, templateId: string): Promise<LibraryTemplate> {
    const response = await this.api.get<LibraryTemplate>(`/admin/v1/template-library/templates/${regulation}/${templateId}`);
    return response.data;
  }

  async getAvailableGeographies(): Promise<GeographyInfo[]> {
    const response = await this.api.get<GeographyInfo[]>('/admin/v1/template-library/geographies');
    return response.data;
  }

  async getAvailableRegulations(): Promise<RegulationInfo[]> {
    const response = await this.api.get<RegulationInfo[]>('/admin/v1/template-library/regulations');
    return response.data;
  }

  async getAvailableCategories(): Promise<CategoryInfo[]> {
    const response = await this.api.get<CategoryInfo[]>('/admin/v1/template-library/categories');
    return response.data;
  }

  async importLibraryTemplate(request: ImportTemplateRequest): Promise<Template> {
    const response = await this.api.post<Template>('/admin/v1/template-library/import', request);
    return response.data;
  }

  async saveToLibrary(request: SaveToLibraryRequest): Promise<LibraryTemplate> {
    const response = await this.api.post<LibraryTemplate>('/admin/v1/template-library/save', request);
    return response.data;
  }

  // ========================================
  // Profile Management
  // ========================================

  async getProfile(): Promise<UserProfileResponse> {
    const response = await this.api.get<UserProfileResponse>('/api/v1/admin/profile');
    return response.data;
  }

  async changePassword(request: ChangePasswordRequest): Promise<ProfileUpdateResponse> {
    const response = await this.api.post<ProfileUpdateResponse>('/api/v1/admin/profile/change-password', request);
    return response.data;
  }

  async requestEmailChange(request: { newEmail: string; password: string }): Promise<ProfileUpdateResponse> {
    const response = await this.api.post<ProfileUpdateResponse>('/api/v1/admin/profile/request-email-change', request);
    return response.data;
  }

  async verifyEmailChange(request: { otp: string; verificationId: string }): Promise<ProfileUpdateResponse> {
    const response = await this.api.post<ProfileUpdateResponse>('/api/v1/admin/profile/verify-email-change', {
      ...request,
      changeType: 'EMAIL'
    });
    return response.data;
  }

  async requestMobileChange(request: { countryCode: string; newMobile: string; password: string }): Promise<ProfileUpdateResponse> {
    const response = await this.api.post<ProfileUpdateResponse>('/api/v1/admin/profile/request-mobile-change', request);
    return response.data;
  }

  async verifyMobileChange(request: { otp: string; verificationId: string }): Promise<ProfileUpdateResponse> {
    const response = await this.api.post<ProfileUpdateResponse>('/api/v1/admin/profile/verify-mobile-change', {
      ...request,
      changeType: 'MOBILE'
    });
    return response.data;
  }

  // ========================================
  // Session Management
  // ========================================

  async getActiveSessions(): Promise<AdminSession[]> {
    const response = await this.api.get<AdminSession[]>('/api/v1/admin/sessions/active');
    return response.data;
  }

  async getPastSessions(limit: number = 5): Promise<AdminSession[]> {
    const response = await this.api.get<AdminSession[]>('/api/v1/admin/sessions/past', { params: { limit } });
    return response.data;
  }

  async terminateSession(sessionId: string): Promise<{ message: string }> {
    const response = await this.api.delete<{ message: string }>(`/api/v1/admin/sessions/${sessionId}`);
    return response.data;
  }

  async logoutAllOtherSessions(currentSessionId?: string): Promise<{ message: string; terminatedCount: number }> {
    const response = await this.api.post<{ message: string; terminatedCount: number }>(
      '/api/v1/admin/sessions/logout-all-others',
      currentSessionId ? { currentSessionId } : {}
    );
    return response.data;
  }

  // ========================================
  // Settings - API Key Management
  // ========================================

  async regenerateApiKey(): Promise<{ message: string; apiKey: string }> {
    const response = await this.api.post<{ message: string; apiKey: string }>('/api/v1/admin/settings/regenerate-api-key');
    return response.data;
  }

  // ========================================
  // Region Management
  // ========================================

  async getAllRegions(activeOnly: boolean = false): Promise<Region[]> {
    const response = await this.api.get<Region[]>('/admin/v1/regions', {
      params: { activeOnly }
    });
    return response.data;
  }

  async getRegion(regionCode: string): Promise<Region> {
    const response = await this.api.get<Region>(`/admin/v1/regions/${regionCode}`);
    return response.data;
  }

  async createRegion(request: RegionRequest): Promise<Region> {
    const response = await this.api.post<Region>('/admin/v1/regions', request);
    return response.data;
  }

  async updateRegion(regionCode: string, request: RegionRequest): Promise<Region> {
    const response = await this.api.put<Region>(`/admin/v1/regions/${regionCode}`, request);
    return response.data;
  }

  async activateRegion(regionCode: string): Promise<Region> {
    const response = await this.api.post<Region>(`/admin/v1/regions/${regionCode}/activate`);
    return response.data;
  }

  async deactivateRegion(regionCode: string): Promise<Region> {
    const response = await this.api.post<Region>(`/admin/v1/regions/${regionCode}/deactivate`);
    return response.data;
  }

  async deleteRegion(regionCode: string): Promise<void> {
    await this.api.delete(`/admin/v1/regions/${regionCode}`);
  }

  async validateRegion(regionCode: string): Promise<RegionValidationResponse> {
    const response = await this.api.get<RegionValidationResponse>(`/admin/v1/regions/${regionCode}/validate`);
    return response.data;
  }

  async getRegionsByFramework(framework: string): Promise<Region[]> {
    const response = await this.api.get<Region[]>(`/admin/v1/regions/by-framework/${framework}`);
    return response.data;
  }

  async getCountryToAwsRegionMappings(): Promise<Record<string, string>> {
    const response = await this.api.get<Record<string, string>>('/admin/v1/regions/mappings/aws-regions');
    return response.data;
  }

  async getCountryToFrameworkMappings(): Promise<Record<string, string>> {
    const response = await this.api.get<Record<string, string>>('/admin/v1/regions/mappings/frameworks');
    return response.data;
  }

  async getRegionSuggestion(countryCode: string): Promise<RegionSuggestion> {
    const response = await this.api.get<RegionSuggestion>(`/admin/v1/regions/suggest/${countryCode}`);
    return response.data;
  }

  // ========================================
  // Notification Template Management
  // ========================================

  async getNotificationTemplates(): Promise<NotificationTemplateItem[]> {
    const response = await this.api.get<ApiResponseWrapper<NotificationTemplateItem[]>>('/admin/v1/notification-templates');
    return response.data.data;
  }

  async getNotificationTemplate(key: string): Promise<NotificationTemplateItem> {
    const encodedKey = encodeURIComponent(key);
    const response = await this.api.get<ApiResponseWrapper<NotificationTemplateItem>>(`/admin/v1/notification-templates/${encodedKey}`);
    return response.data.data;
  }

  async updateNotificationTemplate(key: string, data: { emailSubject: string; emailBody: string; smsBody: string }): Promise<NotificationTemplateItem> {
    const encodedKey = encodeURIComponent(key);
    const response = await this.api.put<ApiResponseWrapper<NotificationTemplateItem>>(`/admin/v1/notification-templates/${encodedKey}`, data);
    return response.data.data;
  }

  async deleteNotificationTemplate(key: string): Promise<void> {
    const encodedKey = encodeURIComponent(key);
    await this.api.delete(`/admin/v1/notification-templates/${encodedKey}`);
  }

  // ========================================
  // Data Erasure Management
  // ========================================

  async getErasurePending(): Promise<ErasureRequest[]> {
    const response = await this.api.get<ErasureRequest[]>('/admin/v1/erasure/pending');
    return response.data;
  }

  async getErasureStats(): Promise<ErasureStats> {
    const response = await this.api.get<ErasureStats>('/admin/v1/erasure/stats');
    return response.data;
  }

  async confirmErasure(consentId: string, data: { confirmedBy: string; notes: string }): Promise<void> {
    await this.api.post(`/admin/v1/erasure/${consentId}/confirm`, data);
  }

  async getErasureDetails(consentId: string): Promise<ErasureRequest> {
    const response = await this.api.get<ErasureRequest>(`/admin/v1/erasure/${consentId}`);
    return response.data;
  }
}

// Data Erasure Types
export interface ErasureRequest {
  consentId: string;
  tenantId: string;
  dataOwnerId: string;
  status: string;
  withdrawnAt?: string;
  withdrawalReason?: string;
  expiresAt?: string;
  dataErasureRequired: boolean;
  dataErasureConfirmedAt?: string;
  dataErasureConfirmedBy?: string;
  dataErasureNotes?: string;
  templateId?: string;
  formId?: string;
  regionCode?: string;
}

export interface ErasureStats {
  totalRequiringErasure: number;
  pendingConfirmation: number;
  confirmed: number;
  overdue: number;
}

// Profile Types
export interface UserProfileResponse {
  userId: string;
  tenantId: string;
  name: string;
  email: string;
  mobile?: string;
  role: string;
  active: boolean;
  createdAt: string;
  lastLoginAt?: string;
  passwordLastChangedAt?: string;
  twoFactorEnabled: boolean;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ProfileUpdateResponse {
  success: boolean;
  message: string;
  verificationId?: string;
}

export interface AdminSession {
  tenantId: string;
  sessionId: string;
  userId: string;
  deviceInfo: string;
  ipAddress: string;
  location: string;
  userAgent: string;
  createdAt: string;
  lastActivityAt: string;
  expiresAt: string;
  active: boolean;
}

// Signup Request Types
export interface SignupRequestDetails {
  requestId: string;
  status: string;
  organizationName: string;
  organizationWebsite?: string;
  organizationType?: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  contactRole?: string;
  expectedUsers?: string;
  useCase?: string;
  privacyPolicyAccepted?: boolean;
  termsOfServiceAccepted?: boolean;
  ipAddress?: string;
  submittedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  createdTenantId?: string;
  createdTenantSlug?: string;
  tenantCreatedAt?: string;
}

export interface SearchParams {
  query?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedSignupResponse {
  data: SignupRequestDetails[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface ApproveSignupResponse {
  message: string;
  request: SignupRequestDetails;
  tenantId: string;
  tenantSlug: string;
}

// ========================================
// User & Invitation Types
// ========================================

export interface AdminUser {
  userId: string;
  tenantId: string;
  email: string;
  name?: string;
  role: 'ADMIN' | 'CONSENT_MANAGER' | 'VIEWER';
  status?: string;
  projectId?: string;
  active?: boolean;
  createdAt?: string;
  lastLoginAt?: string;
  invitedBy?: string;
}

export interface UserInvitation {
  invitationId: string;
  tenantId: string;
  email: string;
  name?: string;
  role: 'ADMIN' | 'CONSENT_MANAGER' | 'VIEWER';
  projectId?: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
  invitedBy?: string;
  invitedByName?: string;
  invitedAt?: string;
  expiresAt?: string;
  acceptedAt?: string;
  createdUserId?: string;
  invitationLink?: string; // Full URL to accept invitation
}

export interface InviteUserRequest {
  email: string;
  name?: string;
  role: 'ADMIN' | 'CONSENT_MANAGER' | 'VIEWER';
  projectId?: string;
  customMessage?: string;
}

export interface AcceptInvitationRequest {
  token: string;
  password: string;
  name?: string;
  privacyPolicyAccepted: boolean;
  termsOfServiceAccepted: boolean;
}

export interface InvitationDetails {
  invitation: UserInvitation;
  tenantName: string;
  tenantSlug: string;
}

// ========================================
// Analytics Types
// ========================================

export interface TrendDataPoint {
  date: string;
  count: number;
}

export interface ConsentTrendsResponse {
  period: string;
  trend: TrendDataPoint[];
  total: number;
}

export interface BreakdownItem {
  status?: string;
  templateId?: string;
  formId?: string;
  count: number;
}

export interface PurposeBreakdownResponse {
  breakdown: BreakdownItem[];
  totalStatuses: number;
  note?: string;
}

export interface TemplateBreakdownResponse {
  topTemplates: BreakdownItem[];
  totalTemplates: number;
}

export interface ProjectBreakdownResponse {
  topForms: BreakdownItem[];
  totalForms: number;
  note?: string;
}

export interface ConversionMetricsResponse {
  total: number;
  completed: number;
  pending: number;
  rejected: number;
  conversionRate: string;
  avgCompletionTimeMs: number;
  note?: string;
}

// ========================================
// Audit Log Types
// ========================================

export interface AuditLogDetails {
  auditId: string;
  timestamp: string;
  action: string;
  entityType: string;
  entityId: string;
  actorId?: string;
  actorType?: string;
  actorEmail?: string;
  actorRole?: string;
  ipAddress?: string;
  userAgent?: string;
  requestId?: string;
  sessionId?: string;
  changeDescription?: string;
  result: string;
  errorMessage?: string;
  httpStatusCode?: number;
  gdprRelevant?: boolean;
  dpdpRelevant?: boolean;
  complianceNotes?: string;
  metadata?: Record<string, string>;
  createdAt?: string;
}

// ========================================
// Withdrawal Request Types
// ========================================

export interface WithdrawalRequestDetails {
  withdrawalId: string;
  tenantId: string;
  consentRecordId: string;
  dataOwnerId: string;
  reason?: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'REJECTED';
  requestedBy?: string;
  processedBy?: string;
  requestedAt: string;
  processedAt?: string;
  verified?: boolean;
  verificationMethod?: string;
  verificationDetails?: string;
  dataErasureRequired?: boolean;
  dataErasureCompleted?: boolean;
  notes?: string;
  rejectionReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ========================================
// Template Library Types
// ========================================

export interface LibraryTemplate {
  regulation: string;
  templateId: string;
  geography: string;
  category: string;
  name: string;
  description: string;
  tags?: string[];
  version: string;
  featured?: boolean;
  usageCount?: number;
  dataCategories?: string[];
  primaryPurposes?: string[];
  recommendedExpiryDays?: number;
  requiresReconsent?: boolean;
  legalBasis?: string;
  complianceNotes?: string;
  mandatoryFields?: string[];
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GeographyInfo {
  code: string;
  displayName: string;
  templateCount: number;
}

export interface RegulationInfo {
  code: string;
  displayName: string;
  templateCount: number;
}

export interface CategoryInfo {
  code: string;
  displayName: string;
  templateCount: number;
}

export interface ImportTemplateRequest {
  regulation: string;
  libraryTemplateId: string;
  projectId?: string;
  dataCollectorId?: string;
}

export interface SaveToLibraryRequest {
  templateId: string;
  regulation: string;
  geography: string;
  category: string;
  name: string;
  description?: string;
  tags?: string[];
  legalBasis?: string;
  complianceNotes?: string;
  mandatoryFields?: string[];
}

// ========================================
// Region Types
// ========================================

export interface Region {
  regionCode: string;
  displayName: string;
  description?: string;
  parentRegionCode?: string;
  active: boolean;
  
  // AWS Infrastructure
  awsRegion: string;
  tableSuffix?: string;
  s3Bucket?: string;
  infrastructureReady: boolean;
  
  // Compliance
  primaryComplianceFramework?: string;
  complianceFrameworks?: string[];
  dataResidencyLevel?: 'STRICT' | 'PREFERRED' | 'FLEXIBLE';
  allowsCrossBorderTransfer?: boolean;
  approvedTransferDestinations?: string[];
  defaultRetentionDays?: number;
  maxRetentionDays?: number;
  
  // Consent Requirements
  minimumConsentAge?: number;
  defaultConsentExpiryDays?: number;
  requiresExplicitOptIn?: boolean;
  requiresDoubleOptIn?: boolean;
  requiredDocumentTypes?: string[];
  easyWithdrawalRequired?: boolean;
  
  // Localization
  primaryLanguage?: string;
  supportedLanguages?: string[];
  timezone?: string;
  dateFormat?: string;
  
  // Authority
  supervisoryAuthority?: string;
  authorityWebsite?: string;
  requiresDpo?: boolean;
  regionalDpoEmail?: string;
  
  // Audit
  createdAt?: string;
  updatedAt?: string;
  
  // Statistics
  tenantCount?: number;
  consentRecordCount?: number;
  dataOwnerCount?: number;
  
  // Metadata
  metadata?: Record<string, string>;
}

export interface RegionRequest {
  regionCode: string;
  displayName: string;
  description?: string;
  parentRegionCode?: string;
  awsRegion?: string;
  tableSuffix?: string;
  s3Bucket?: string;
  kmsKeyArn?: string;
  primaryComplianceFramework?: string;
  complianceFrameworks?: string[];
  dataResidencyLevel?: string;
  allowsCrossBorderTransfer?: boolean;
  approvedTransferDestinations?: string[];
  defaultRetentionDays?: number;
  maxRetentionDays?: number;
  minimumConsentAge?: number;
  defaultConsentExpiryDays?: number;
  requiresExplicitOptIn?: boolean;
  requiresDoubleOptIn?: boolean;
  requiredDocumentTypes?: string[];
  easyWithdrawalRequired?: boolean;
  primaryLanguage?: string;
  supportedLanguages?: string[];
  timezone?: string;
  dateFormat?: string;
  supervisoryAuthority?: string;
  authorityWebsite?: string;
  requiresDpo?: boolean;
  regionalDpoEmail?: string;
  metadata?: Record<string, string>;
}

export interface RegionValidationResponse {
  valid: boolean;
  regionCode: string;
  awsRegion?: string;
  dynamoDbReady: boolean;
  s3Ready: boolean;
  kmsReady: boolean;
  complianceConfigured: boolean;
  localizationConfigured: boolean;
  errors: string[];
  warnings: string[];
  recommendations: string[];
}

export interface RegionSuggestion {
  countryCode: string;
  awsRegion: string;
  complianceFramework?: string;
}

// ========================================
// Notification Template Types
// ========================================

export interface NotificationTemplateItem {
  tenantId: string;
  templateKey: string;
  languageCode: string;
  emailSubject: string;
  emailBody: string;
  smsBody: string;
  updatedAt?: string;
  updatedBy?: string;
}

interface ApiResponseWrapper<T> {
  success: boolean;
  data: T;
  error?: string;
  timestamp: number;
}

export const adminApi = new AdminApiService();
